/**
 * `/machines/:machineId` — the machine page. Port of the demo's
 * `openMachine(id)` (line 2176) and its tab bodies (`machineTab`, line 2199).
 *
 * Header: model and class, site, division › subclass, engine hours, condition
 * band, availability, cost per operating hour, and who carries the cost of a
 * job on this machine. Prev / next — and ← / → on the keyboard — step through
 * the machine's own subclass, as the demo's ◀ ▶ pair does; the jump list
 * between them reaches any of the 100 machines (demo `#mh-pick`).
 *
 * Twelve tabs, each loading only what it needs, each addressable as `?tab=<id>`
 * (the default, Illustration, carries no parameter; the Reliability tab adds
 * `?view=spc|tarp|fmea|control`):
 *
 * | Tab | Data |
 * |---|---|
 * | Illustration | eager register; turntable frames / 3D model on demand (L7's tab body) |
 * | Condition | eager |
 * | Machine history | `work-orders`, then `telemetry` / `service` / `comms` merged in |
 * | Telemetry | `telemetry`; `telemetry-live` at a live site; `rms` for S-09 |
 * | Operators | `useReliability()` — who drove each shift, hours vs events, root cause, training |
 * | Reliability | `useReliability()` — SPC, TARP, FMEA, control plan |
 * | Cost | `work-orders` + `costs` |
 * | Energy | `telemetry` |
 * | Parts & stock | `parts` |
 * | Maximo record | `maximo/<id>.json` |
 * | RMS | `rms` |
 * | Documents | `doc-index`, then one lake document on demand |
 *
 * Selecting a component — on the illustration or in the register — is page
 * state, so a click on a callout filters the work history, which is the demo's
 * own cross-highlight.
 *
 * ## Declared omissions
 *
 * - **Draggable illustration callouts** — see `shared/viz/MachineIllustration.tsx`.
 *
 * @see src/versions/v1/modules/fleet/MachineScene.transform.ts
 * @see docs/logic/fleet/machine-page.md
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, HardHat } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { hasMachine, MINES } from '../../data/core';
import { CONDITION_BAND_LABEL } from '../../schema/machine';
import { unpackTable } from '../../shared/columnar';
import { healthOf, payFor, type PayerVerdict } from '../../shared/oem-engine';
import { useOemData, type ShiftDailyRow } from '../../shared/useOemData';
import './palette';
import { MachineConditionTab } from './MachineConditionTab';
import { MachineCostTab } from './MachineCostTab';
import { MachineDocumentsTab } from './MachineDocumentsTab';
import { MachineHistoryTab } from './MachineHistoryTab';
import { MachineOperatorsTab } from './MachineOperatorsTab';
import { MachineReliabilityTab } from './MachineReliabilityTab';
import { MachineIllustrationTab } from './MachineIllustrationTab';
import { MachineRecordTab, useMachineRecord } from './MachineRecordTab';
import { MachineEnergyTab } from './MachineEnergyTab';
import { MachinePartsTab } from './MachinePartsTab';
import { MachineRmsTab } from './MachineRmsTab';
import { conditionRegister, machineHeader, machineJumpOptions } from './MachineScene.transform';
import { MachineTelemetryTab } from './MachineTelemetryTab';
import { useArrowStep } from './useArrowStep';

const TABS = [
  { id: 'illustration', label: 'Illustration' },
  { id: 'condition', label: 'Condition register' },
  { id: 'history', label: 'Machine history' },
  { id: 'telemetry', label: 'Telemetry' },
  { id: 'operators', label: 'Operators' },
  { id: 'reliability', label: 'Reliability' },
  { id: 'cost', label: 'Cost' },
  { id: 'energy', label: 'Energy' },
  { id: 'parts', label: 'Parts & stock' },
  { id: 'record', label: 'Maximo record' },
  { id: 'rms', label: 'RMS' },
  { id: 'documents', label: 'Documents' },
] as const;

type MachineTab = (typeof TABS)[number]['id'];

const DEFAULT_TAB: MachineTab = 'illustration';

/** `?tab=` → a known tab id, else the default. Unknown ids fall back rather than blank the page. */
function parseTab(raw: string | null): MachineTab {
  return TABS.some((t) => t.id === raw) ? (raw as MachineTab) : DEFAULT_TAB;
}

/** Demo line 2178. The jump list's 100 options — eager data, built once. */
const JUMP_OPTIONS = machineJumpOptions();

/** Tone token → the band pill class that renders it. */
const PAYER_CLASS: Readonly<Record<string, string>> = {
  good: 'band-good',
  info: 'band-fair',
  warn: 'band-poor',
  crit: 'band-vpoor',
  accent: 'band-major',
};

export function MachineScene() {
  const { machineId = '' } = useParams<{ machineId: string }>();
  const id = decodeURIComponent(machineId);
  const known = hasMachine(id);

  const navigate = useNavigate();
  // The tab lives in the URL (`?tab=`) so chat answers and other pages can
  // deep-link: `/machines/T-09?tab=reliability&view=spc`. The default tab
  // carries no parameter.
  const [params, setParams] = useSearchParams();
  const tab = parseTab(params.get('tab'));
  const setTab = (next: MachineTab) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === DEFAULT_TAB) p.delete('tab');
        else p.set('tab', next);
        // `view` is the Reliability tab's sub-view; it means nothing elsewhere.
        if (next !== 'reliability') p.delete('view');
        return p;
      },
      { replace: true },
    );
  /** Step to another machine, keeping the open tab (and sub-view). */
  const goTo = (machine: string) => {
    const search = params.toString();
    navigate({ pathname: `/machines/${machine}`, search: search ? `?${search}` : '' });
  };
  const [component, setComponent] = useState<string | null>(null);

  const telemetry = useOemData('telemetry');
  const record = useMachineRecord(known ? id : '');

  const shiftCtx = useMemo(
    () =>
      telemetry.data
        ? { shiftDaily: unpackTable<ShiftDailyRow>(telemetry.data.shift_daily) }
        : { shiftDaily: [] },
    [telemetry.data],
  );

  const header = useMemo(
    () => (known ? machineHeader(id, shiftCtx) : null),
    [known, id, shiftCtx],
  );
  const register = useMemo(() => (known ? conditionRegister(id) : []), [known, id]);
  const health = useMemo(() => (known ? healthOf(id) : []), [known, id]);

  const payer: PayerVerdict | null = useMemo(
    () => (record.record ? payFor(id, { maximo: { [id]: record.record.mx } }) : null),
    [record.record, id],
  );

  // Demo line 2171: ← / → step through the subclass, as the buttons do.
  useArrowStep(
    header
      ? (delta) => goTo(delta < 0 ? header.previousId : header.nextId)
      : null,
  );

  if (!known || !header) {
    return (
      <SurfacePageLayout width="content">
        <SurfacePageBody as="div" className="space-y-6">
          <EmptyState
            icon={<HardHat className="mx-auto h-8 w-8 text-zinc-400" strokeWidth={1.6} />}
            title={`No machine ${id || 'selected'}`}
            description="That id is not in the 100-machine fleet register."
            action={
              <Link
                to="/fleet"
                className="text-sm text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
              >
                Back to fleet health
              </Link>
            }
          />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const f = header.machine;
  const mine = MINES[f.mine];

  /** Selecting a component sends the reader to the history filtered by it. */
  const selectComponent = (next: string | null) => {
    setComponent(next);
    if (next) setTab('history');
  };

  return (
    <SurfacePageLayout width="full">
      <Commentable name="MachineScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-6" as="div">
            <SurfacePageHeader
              kicker={`${header.mineName} · ${f.division} › ${f.subclass}`}
              title={header.title}
              description={header.standfirst}
              actions={
                <div className="flex items-center gap-2">
                  <nav aria-label="Step through this subclass" className="flex items-center gap-1">
                    <button
                      type="button"
                      title={`Previous ${f.subclass.toLowerCase()} (←)`}
                      aria-label={`Previous ${f.subclass.toLowerCase()}`}
                      onClick={() => goTo(header.previousId)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--glass-border)] text-muted transition-colors hover:bg-[var(--glass-surface-strong)]"
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <label className="sr-only" htmlFor="machine-jump">
                      Jump to machine
                    </label>
                    <select
                      id="machine-jump"
                      value={f.machine_id}
                      onChange={(e) => goTo(e.target.value)}
                      className="h-8 max-w-[16rem] rounded-md border border-[var(--glass-border)] bg-[var(--glass-surface-strong)] px-2 text-xs text-zinc-900 dark:text-zinc-100"
                    >
                      {JUMP_OPTIONS.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      title={`Next ${f.subclass.toLowerCase()} (→)`}
                      aria-label={`Next ${f.subclass.toLowerCase()}`}
                      onClick={() => goTo(header.nextId)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--glass-border)] text-muted transition-colors hover:bg-[var(--glass-surface-strong)]"
                    >
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </nav>
                  <span className="mono px-1 text-xs text-muted">
                    {header.position} / {header.peers} {f.subclass.toLowerCase()}
                  </span>
                  <HelpAnchor doc="fleet/machine-page" />
                </div>
              }
            />


            <Commentable name="MachineSummary">
              <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-border)] @2xl:grid-cols-4">
                <Stat label="Condition">
                  <span className={`band-pill band-${header.band}`}>
                    {CONDITION_BAND_LABEL[header.band]}
                  </span>
                </Stat>
                <Stat label="Availability · week to 06 Sep">
                  <span className="mono">
                    {header.availability != null ? `${header.availability}%` : '—'}
                  </span>
                </Stat>
                <Stat label="Cost per operating hour">
                  <span className="mono">
                    {header.costPerOpHour != null ? `A$${header.costPerOpHour}` : '—'}
                  </span>
                </Stat>
                <Stat label="Who pays">
                  {payer ? (
                    <span className={`band-pill ${PAYER_CLASS[payer.tone] ?? 'band-fair'}`}>
                      {payer.label}
                    </span>
                  ) : (
                    <span className="text-sm text-muted">
                      {record.error ? 'Record unavailable' : 'Loading…'}
                    </span>
                  )}
                </Stat>
              </dl>
            </Commentable>

            {/* B3: the ask chips sit under the four tiles, not above them. */}
            <AskStrip view="machine" machineId={f.machine_id} />

            <Commentable name="MachineTabs">
              <SurfacePageTabs
                items={TABS}
                value={tab}
                onChange={setTab}
                aria-label="Machine detail"
              />
            </Commentable>

            <Commentable name="MachineTabBody">
              <div>
                {tab === 'illustration' ? (
                  <MachineIllustrationTab
                    machine={f}
                    health={health}
                    selected={component}
                    onSelect={selectComponent}
                  />
                ) : null}

                {tab === 'condition' ? (
                  <MachineConditionTab
                    rows={register}
                    selected={component}
                    onSelect={selectComponent}
                  />
                ) : null}

                {tab === 'history' ? (
                  <MachineHistoryTab
                    machineId={id}
                    band={header.band}
                    component={component}
                    onClearComponent={() => setComponent(null)}
                  />
                ) : null}

                {tab === 'operators' ? <MachineOperatorsTab machineId={id} /> : null}

                {tab === 'reliability' ? <MachineReliabilityTab machineId={id} /> : null}

                {tab === 'cost' ? (
                  <MachineCostTab
                    machineId={id}
                    mineName={header.mineName}
                    cls={f.cls}
                    model={f.model}
                  />
                ) : null}

                {tab === 'telemetry' ? (
                  <MachineTelemetryTab
                    machineId={id}
                    band={header.band}
                    isLiveSite={mine.data_mode === 'Live'}
                  />
                ) : null}

                {tab === 'energy' ? (
                  <MachineEnergyTab machineId={id} mineName={header.mineName} cls={f.cls} />
                ) : null}

                {tab === 'parts' ? <MachinePartsTab machineId={id} /> : null}

                {tab === 'record' ? <MachineRecordTab machineId={id} /> : null}

                {tab === 'rms' ? (
                  <MachineRmsTab
                    machineId={id}
                    mineName={header.mineName}
                    isLiveSite={mine.data_mode === 'Live'}
                  />
                ) : null}

                {tab === 'documents' ? (
                  <MachineDocumentsTab machineId={id} model={f.model} />
                ) : null}
              </div>
            </Commentable>
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="bg-[var(--glass-surface-strong)] px-4 py-3">
      <dt className="text-xs uppercase tracking-wider text-muted">{label}</dt>
      <dd className="mt-1 text-lg font-semibold text-zinc-900 dark:text-zinc-100">{children}</dd>
    </div>
  );
}
